import React from 'react';
import { Drawer, Button, Select, DatePicker } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { COLORS } from '../../../constants/colors';
import { FilterChip, FilterSheetSection } from '../NewStyled';

const { Option } = Select;
const { RangePicker } = DatePicker;

const BookingFilterDrawer = ({
  visible,
  onClose,
  filters,
  onFilterChange,
  eventTypes,
  onExport,
  hasBookings,
  isMobile = false
}) => {
  const handleApply = () => {
    onClose();
  };

  const handleClear = () => {
    onFilterChange('search', '');
    onFilterChange('status', undefined);
    onFilterChange('eventType', undefined);
    onFilterChange('dateRange', null);
  };

  // Mobile bottom sheet: Date range + location chips, matching the app design.
  if (isMobile) {
    return (
      <Drawer
        placement="bottom"
        onClose={onClose}
        open={visible}
        height="auto"
        closable={false}
        styles={{
          content: { borderTopLeftRadius: 16, borderTopRightRadius: 16 },
          body: { padding: '20px 16px 24px' }
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <span style={{ fontSize: 20, fontWeight: 700, color: COLORS.textDark }}>Filters</span>
          <button
            type="button"
            onClick={handleClear}
            style={{ background: 'none', border: 'none', color: COLORS.$primaryBlue, fontSize: 15, fontWeight: 600, cursor: 'pointer', padding: 0 }}
          >
            Clear all
          </button>
        </div>

        <FilterSheetSection>
          <div className="sheet-label">Date Range</div>
          <RangePicker
            style={{ width: '100%', height: 44 }}
            value={filters.dateRange}
            onChange={(dates) => onFilterChange('dateRange', dates)}
            format="MMM D, YYYY"
            placeholder={['Start date', 'End date']}
            allowClear
          />
        </FilterSheetSection>

        <FilterSheetSection>
          <div className="sheet-label">By Location</div>
          <div className="chip-wrap">
            {Array.isArray(eventTypes) && eventTypes.map((eventType) => {
              const active = filters.eventType === eventType._id;
              return (
                <FilterChip
                  key={eventType._id}
                  type="button"
                  $active={active}
                  onClick={() => onFilterChange('eventType', active ? undefined : eventType._id)}
                >
                  <span className="dot" style={{ background: eventType.color || COLORS.$primaryBlue }} />
                  {eventType.name}
                  {typeof eventType.bookingCount === 'number' && (
                    <span className="count">{eventType.bookingCount}</span>
                  )}
                </FilterChip>
              );
            })}
          </div>
        </FilterSheetSection>

        <Button
          type="primary"
          block
          onClick={handleApply}
          style={{ height: 48, borderRadius: 10, fontWeight: 600, fontSize: 16, background: COLORS.$primaryBlue, borderColor: COLORS.$primaryBlue }}
        >
          Apply filters
        </Button>
      </Drawer>
    );
  }

  return (
    <Drawer
      title="Filters & Actions"
      placement="right"
      onClose={onClose}
      open={visible}
      width={300}
      styles={{
        body: { padding: '16px', display: 'flex', flexDirection: 'column', height: '100%' }
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', flex: 1 }}>
        {/* Status Filter */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <label style={{
            fontSize: '13px',
            fontWeight: '600',
            color: COLORS.textDark,
            marginBottom: '4px'
          }}>
            Status
          </label>
          <Select
            placeholder="All Statuses"
            style={{ width: '100%', height: '48px' }}
            value={filters.status}
            onChange={(value) => onFilterChange('status', value)}
            allowClear
          >
            <Option value="confirmed">Confirmed</Option>
            <Option value="completed">Completed</Option>
            <Option value="cancelled">Cancelled</Option>
            <Option value="no-show">No Show</Option>
            <Option value="archived">Archived</Option>
          </Select>
        </div>
        
        {/* Event Type Filter */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <label style={{
            fontSize: '13px',
            fontWeight: '600',
            color: COLORS.textDark,
            marginBottom: '4px'
          }}>
            Event Type
          </label>
          <Select
            placeholder="All Events"
            style={{ width: '100%', height: '48px' }}
            value={filters.eventType}
            onChange={(value) => onFilterChange('eventType', value)}
            allowClear
          >
            {Array.isArray(eventTypes) && eventTypes.map(eventType => (
              <Option key={eventType._id} value={eventType._id}>
                {eventType.name}
              </Option>
            ))}
          </Select>
        </div>

        {/* Date Range Filter */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <label style={{
            fontSize: '13px',
            fontWeight: '600',
            color: COLORS.textDark,
            marginBottom: '4px'
          }}>
            Event Date Range
          </label>
          <RangePicker
            style={{ width: '100%', height: '48px' }}
            value={filters.dateRange}
            onChange={(dates) => onFilterChange('dateRange', dates)}
            format="MMM D, YYYY"
            placeholder={['Start date', 'End date']}
            allowClear
          />
        </div>
        
        {/* Action Buttons */}
        <div style={{
          marginTop: '24px',
          paddingTop: '16px',
          borderTop: `1px solid ${COLORS.border}`,
          display: 'flex',
          gap: '8px'
        }}>
          <Button
            onClick={handleClear}
            style={{ flex: 1 }}
          >
            Clear
          </Button>
          <Button
            type="primary"
            onClick={handleApply}
            style={{
              flex: 1,
              background: COLORS.$primaryBlue,
              borderColor: COLORS.$primaryBlue
            }}
          >
            Apply
          </Button>
        </div>
        
        {/* Download Section */}
        <div style={{ 
          marginTop: 'auto',
          paddingTop: '24px', 
          borderTop: `1px solid ${COLORS.border}`
        }}>
          <Button
            block
            icon={<FontAwesomeIcon icon={faDownload} />}
            onClick={onExport}
            disabled={!hasBookings}
            style={{
              background: hasBookings ? '#262626' : '#f5f5f5',
              borderColor: hasBookings ? '#262626' : '#d9d9d9',
              color: hasBookings ? 'white' : '#00000040'
            }}
          >
            Export Data (CSV)
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

export default BookingFilterDrawer;
